import {
  IMarketingInsight,
  IMarketingInsightPart,
  marketingInsightPart,
} from './marketingInsightsModel';
import {
  formatMarketingCount,
  formatMarketingPosition,
  formatMarketingPct,
  formatMarketingSales,
  IMarketingSortOf,
  IMarketingText,
  marketingJsonNumber,
  marketingNumber,
  marketingParseJson,
  MARKETING_DASH,
} from './marketingModel';
import {
  IMarketingSeoCluster,
  IMarketingSeoFunnelRow,
  IMarketingSeoOpportunity,
  IMarketingSeoZoneWeek,
} from './seoTypes';
import { MarketingDate, MarketingJson } from './types';

/**
 * ТЗ 0103 — вкладка «SEO» (design-erp §4): подвкладки, значения адреса и
 * чистая часть экранов DataForSEO — зоны позиций, изменения, виды идей и
 * возможностей, AI-системы, свежесть снимка, дельта позиции (меньше —
 * лучше), «В план» кластера и выгрузка плана в markdown. Без React, Apollo
 * и i18n — подписи ключами `marketing.seo.*`.
 */

/**
 * Подвкладки SEO (`stab` адреса) — в порядке полосы. Повторное ревью 0103,
 * пакет 2 (P4): «Ссылки» — раздел «Конкурентов» (`#seo-links`), старый
 * `stab=links` переводит `marketingLegacyLinks`.
 */
export const MARKETING_SEO_SUBTABS = [
  'overview',
  'positions',
  'competitors',
  'ideas',
  'ai',
  'demand',
  'engines',
] as const;

export type MarketingSeoSubTab = typeof MARKETING_SEO_SUBTABS[number];

/**
 * Подвкладки на данных DataForSEO — над ними чип свежести снимка
 * («обновляется в ночь на понедельник · следующий …»).
 */
export const MARKETING_SEO_DATAFORSEO_SUBTABS: MarketingSeoSubTab[] = [
  'overview',
  'positions',
  'competitors',
  'ideas',
  'ai',
];

/**
 * Подвкладки на НЕДЕЛЬНЫХ снимках выдачи: у них выбор «Снимок от … ·
 * сравнить с …» (`snap`, `csnap`). «Что написать» — месячная база Labs:
 * недельный выбор к ней не относится (как и к разделу «Ссылки» внутри
 * «Конкурентов» — у него свой месяц).
 */
export const MARKETING_SEO_WEEKLY_SUBTABS: MarketingSeoSubTab[] = [
  'overview',
  'positions',
  'competitors',
  'ai',
];

/** Поисковик «Поисковиков» (`eng`): Google и Bing никогда не складываются. */
export const MARKETING_SEO_ENGINES = ['google', 'bing'] as const;

export type MarketingSeoEngine = typeof MARKETING_SEO_ENGINES[number];

export const MARKETING_DEFAULT_SEO_ENGINE: MarketingSeoEngine = 'google';

export const isMarketingSeoEngine = (
  value: string | null | undefined,
): value is MarketingSeoEngine =>
  Boolean(value) &&
  (MARKETING_SEO_ENGINES as readonly string[]).includes(value as string);

/** Изменение позиции запроса (`chg`): выросли, упали, новые, потеряны. */
export const MARKETING_SEO_CHANGE_FILTERS = [
  'up',
  'down',
  'new',
  'lost',
] as const;

export type MarketingSeoChangeFilter = typeof MARKETING_SEO_CHANGE_FILTERS[number];

export const isMarketingSeoChangeFilter = (
  value: string | null | undefined,
): value is MarketingSeoChangeFilter =>
  Boolean(value) &&
  (MARKETING_SEO_CHANGE_FILTERS as readonly string[]).includes(value as string);

/** Домен конкурента в адресе (`dom`): `coderslab.pl`; мусор — нет. */
export const MARKETING_DOMAIN_RE = /^(?=.{3,253}$)([a-z0-9-]+\.)+[a-z]{2,}$/i;

/* ── Зоны позиций (недельная выдача, глубина 20) ─────────────────────── */

/**
 * Зоны своей позиции в недельной выдаче (`szone`): 1–3, 4–10, 11–20 и «нет
 * в топ-20». Своя шкала, не шкала Search Console (там 21–50 и 51+): у
 * параметра SEO своё имя, чтобы «Поисковики» и «Позиции» не делили фильтр.
 */
export const MARKETING_SEO_ZONES = ['1_3', '4_10', '11_20', 'over_20'] as const;

export type MarketingSeoZone = typeof MARKETING_SEO_ZONES[number];

export const isMarketingSeoZone = (
  value: string | null | undefined,
): value is MarketingSeoZone =>
  Boolean(value) &&
  (MARKETING_SEO_ZONES as readonly string[]).includes(value as string);

/** Поле недельного ряда зоны (`zonesByWeek`, `marketingSeoSeries`). */
export const MARKETING_SEO_ZONE_FIELDS: Readonly<
  Record<MarketingSeoZone, 'top3' | 'pos4to10' | 'pos11to20' | 'beyond20'>
> = {
  '1_3': 'top3',
  '4_10': 'pos4to10',
  '11_20': 'pos11to20',
  over_20: 'beyond20',
};

/** Числа чипов «Позиций» из ответа (`zoneCounts` + `changeCounts`). */
export const marketingSeoCounts = (
  counts: ReadonlyArray<{ value: string; count: number }> | null | undefined,
): Record<string, number> =>
  Object.fromEntries((counts ?? []).map((item) => [item.value, item.count]));

/**
 * Чип «Позиций» — одна строка: зона и изменение взаимоисключающие. Выбран
 * тот, что в адресе (зона приоритетнее: её ставит клик по столбику).
 */
export const marketingSeoPositionsChip = (
  zone: MarketingSeoZone | null,
  change: MarketingSeoChangeFilter | null,
): string | null => zone ?? change ?? null;

/** Клик по чипу «Позиций» → поля адреса (`szone`, `chg`). */
export const marketingSeoPositionsPatch = (
  id: string | null,
): {
  seoZone: MarketingSeoZone | null;
  change: MarketingSeoChangeFilter | null;
} => {
  if (isMarketingSeoZone(id)) return { seoZone: id, change: null };
  if (isMarketingSeoChangeFilter(id)) return { seoZone: null, change: id };
  return { seoZone: null, change: null };
};

/** Сортировки таблицы «Позиций» (`orderBy` сервера, `-` — по убыванию). */
export const MARKETING_SEO_KEYWORD_SORTS = [
  'keyword',
  'volume',
  'position',
  'delta',
  'gsc_clicks',
  'gsc_impressions',
  'difficulty',
] as const;

export type MarketingSeoKeywordSortKey = typeof MARKETING_SEO_KEYWORD_SORTS[number];

/** По умолчанию — самые частотные запросы сверху. */
export const MARKETING_SEO_DEFAULT_ORDER = '-volume';

export const marketingSeoKeywordsOrderBy = (
  sort: IMarketingSortOf<MarketingSeoKeywordSortKey> | null,
): string =>
  sort ? `${sort.desc ? '-' : ''}${sort.key}` : MARKETING_SEO_DEFAULT_ORDER;

/** Строк таблицы «Позиций» за страницу. */
export const MARKETING_SEO_KEYWORDS_PAGE = 50;

/* ── Аргументы запросов из адреса ─────────────────────────────────────── */

/**
 * Бренд адреса (`brand`) → аргумент сервера: `true` / `false` / `null`.
 * Пусто в адресе — `fallback` экрана (повторное ревью 0103, A16): «Позиции»
 * по умолчанию без бренда (`false`), «все» — явным `brand=all` (`null`).
 * У «Обзора» `null` сервер читает как «без бренда» (как «Конкуренты»).
 */
export const marketingSeoBrandArg = (
  brand: string | null | undefined,
  fallback: boolean | null = null,
): boolean | null => {
  if (brand === 'brand') return true;
  if (brand === 'nonbrand') return false;
  if (brand === 'all') return null;
  return fallback;
};

/**
 * Режим бренда «Обзора» SEO для подписи KPI: сервер считает его либо без
 * бренда (по умолчанию, `null` и `false`), либо только по брендовым
 * (`true`); «все» в обзоре нет.
 */
export const marketingSeoOverviewBrand = (
  brand: boolean | null | undefined,
): 'brand' | 'nonbrand' => (brand === true ? 'brand' : 'nonbrand');

/* ── Неделя снимка и свежесть ─────────────────────────────────────────── */

/** Снимок старше — предупреждение (недельный прогон + запас). */
export const MARKETING_SEO_STALE_DAYS = 8;

/** Неделя снимка (понедельник ISO-днём) → «05.10». */
export const marketingSeoWeekLabel = (
  week: MarketingDate | null | undefined,
): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(week ?? '');
  return match ? `${match[3]}.${match[2]}` : MARKETING_DASH;
};

/** «2026-10» → «10.2026». */
export const marketingSeoMonthLabel = (
  month: string | null | undefined,
): string => {
  const match = /^(\d{4})-(\d{2})/.exec(month ?? '');
  return match ? `${match[2]}.${match[1]}` : MARKETING_DASH;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export interface IMarketingSeoFreshness {
  /** Полных дней с проверки снимка; `null` — снимка нет. */
  days: number | null;
  /** Старше `MARKETING_SEO_STALE_DAYS` (или сервер сказал `stale`). */
  stale: boolean;
}

/** Свежесть снимка по времени проверки (`checkedAt`). */
export const marketingSeoFreshness = (
  checkedAt: string | null | undefined,
  now: Date = new Date(),
  serverStale = false,
): IMarketingSeoFreshness => {
  const at = checkedAt ? Date.parse(checkedAt) : Number.NaN;
  if (Number.isNaN(at)) return { days: null, stale: serverStale };
  const days = Math.max(0, Math.floor((now.getTime() - at) / DAY_MS));
  return { days, stale: serverStale || days >= MARKETING_SEO_STALE_DAYS };
};

/* ── Позиции: меньше — лучше ──────────────────────────────────────────── */

export type MarketingSeoTone = 'good' | 'bad' | 'neutral';

/**
 * Тон изменения позиции. `delta` сервера — «рост позиции» (+ — выше в
 * выдаче), поэтому плюс — хорошо, хоть число позиции и уменьшилось.
 */
export const marketingSeoDeltaTone = (
  delta: number | null | undefined,
): MarketingSeoTone => {
  if (delta === null || delta === undefined || delta === 0) return 'neutral';
  return delta > 0 ? 'good' : 'bad';
};

/**
 * Рост позиции между неделями: было 12, стало 7 → +5. Одной стороны нет —
 * `null` (новый или потерянный запрос — не «рост на 13»).
 */
export const marketingSeoPositionGain = (
  previous: number | null | undefined,
  current: number | null | undefined,
): number | null =>
  previous === null ||
  previous === undefined ||
  current === null ||
  current === undefined
    ? null
    : previous - current;

/* ── Конкуренты ───────────────────────────────────────────────────────── */

/** Формат метрики матрицы «метрики × домены». */
export type MarketingSeoMetricFormat =
  | 'share'
  | 'count'
  | 'position'
  | 'clicks';

export interface IMarketingSeoCompetitorMetric {
  id:
    | 'sov'
    | 'top3'
    | 'top10'
    | 'top20'
    | 'avgPosition'
    | 'estClicks'
    | 'labsEtv'
    | 'labsKeywords'
    | 'labsTop10'
    | 'referringDomains'
    | 'backlinks'
    | 'backlinksRank'
    | 'aioCitationShare'
    | 'aiMentionShare'
    | 'aiCitationShare';
  format: MarketingSeoMetricFormat;
  /** Меньше — лучше (средняя позиция): тон ячейки по месту наоборот. */
  lowerBetter?: boolean;
}

/**
 * Строки матрицы конкурентов (идея `CourseUI` seo_soft): неделя выдачи без
 * бренда, база Labs месяца, ссылки, AI. Тон ячейки — место домена в строке.
 */
export const MARKETING_SEO_COMPETITOR_METRICS: ReadonlyArray<IMarketingSeoCompetitorMetric> = [
  { id: 'sov', format: 'share' },
  { id: 'top3', format: 'count' },
  { id: 'top10', format: 'count' },
  { id: 'top20', format: 'count' },
  { id: 'avgPosition', format: 'position', lowerBetter: true },
  { id: 'estClicks', format: 'clicks' },
  { id: 'labsEtv', format: 'clicks' },
  { id: 'labsKeywords', format: 'count' },
  { id: 'labsTop10', format: 'count' },
  { id: 'referringDomains', format: 'count' },
  { id: 'backlinks', format: 'count' },
  { id: 'backlinksRank', format: 'count' },
  { id: 'aioCitationShare', format: 'share' },
  { id: 'aiMentionShare', format: 'share' },
  { id: 'aiCitationShare', format: 'share' },
];

export const MARKETING_SEO_COMPETITOR_METRIC_IDS = MARKETING_SEO_COMPETITOR_METRICS.map(
  (metric) => metric.id,
);

/**
 * Значение для тона «по месту»: у метрик «меньше — лучше» знак меняется
 * (тон `rank` матрицы — «больше — насыщеннее»). Пропуск ≠ ноль (повторное
 * ревью 0103, A6): нет значения — `null` («—»), настоящий 0 — `0` (ячейка
 * «0» без тона). Позиция 0 не бывает — пропуск.
 */
export const marketingSeoMetricToneValue = (
  metric: IMarketingSeoCompetitorMetric,
  value: number | null | undefined,
): number | null => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return null;
  }
  if (metric.lowerBetter) return value > 0 ? 1000 / value : null;
  return Math.max(0, value);
};

/* ── «Что написать» ───────────────────────────────────────────────────── */

/**
 * Чипы «Что написать» (`kind` сервера): страницы нет, страница есть (ниже
 * 10-й), курса нет, вопросы.
 */
export const MARKETING_SEO_IDEA_KINDS = [
  'no_page',
  'weak_page',
  'no_course',
  'questions',
] as const;

export type MarketingSeoIdeaKind = typeof MARKETING_SEO_IDEA_KINDS[number];

export const isMarketingSeoIdeaKind = (
  value: string | null | undefined,
): value is MarketingSeoIdeaKind =>
  Boolean(value) &&
  (MARKETING_SEO_IDEA_KINDS as readonly string[]).includes(value as string);

/** Предложенное действие карточки кластера. */
export const MARKETING_SEO_CLUSTER_ACTIONS = [
  'new_page',
  'improve_page',
  'new_course',
] as const;

/**
 * Намерение запроса / кластера (`seo_intent` сервера): обучение,
 * коммерческий, «узнать», навигационный (бренд чужого продукта — «gpt»,
 * «github»), смешанный (только у кластера и темы).
 */
export const MARKETING_SEO_INTENTS = [
  'course',
  'commercial',
  'informational',
  'navigational',
  'mixed',
] as const;

export type MarketingSeoIntent = typeof MARKETING_SEO_INTENTS[number];

export const isMarketingSeoIntent = (
  value: string | null | undefined,
): value is MarketingSeoIntent =>
  Boolean(value) &&
  (MARKETING_SEO_INTENTS as readonly string[]).includes(value as string);

/**
 * Предложенное действие кластера и темы (`suggestedAction`): новый курс,
 * страница курса, статья, улучшить нашу страницу.
 */
export const MARKETING_SEO_SUGGESTED_ACTIONS = [
  'new_course',
  'course_page',
  'article',
  'improve_page',
] as const;

export type MarketingSeoSuggestedAction = typeof MARKETING_SEO_SUGGESTED_ACTIONS[number];

export const isMarketingSeoSuggestedAction = (
  value: string | null | undefined,
): value is MarketingSeoSuggestedAction =>
  Boolean(value) &&
  (MARKETING_SEO_SUGGESTED_ACTIONS as readonly string[]).includes(
    value as string,
  );

/**
 * Скрытые запросы «Что написать» (`hiddenCounts`): навигационные — без
 * «показать навигационные», исключённые темы — всегда.
 */
export const MARKETING_SEO_HIDDEN_KINDS = ['navigational', 'excluded'] as const;

export type MarketingSeoHiddenKind = typeof MARKETING_SEO_HIDDEN_KINDS[number];

export const marketingSeoHiddenCounts = (
  counts: ReadonlyArray<{ value: string; count: number }> | null | undefined,
): Record<MarketingSeoHiddenKind, number> => {
  const byKind = new Map(
    (counts ?? []).map((item) => [item.value, item.count]),
  );
  return {
    navigational: byKind.get('navigational') ?? 0,
    excluded: byKind.get('excluded') ?? 0,
  };
};

/**
 * Подпись CTR обзора — кодом сервера (`ctrNote`), текст у ERP. Незнакомый
 * код — та же общая подпись: смысл («клики по позиции — оценка») один.
 */
export const MARKETING_SEO_CTR_NOTES = ['ctr_estimate'] as const;

export const marketingSeoCtrNoteKey = (code: string | null | undefined) =>
  code && (MARKETING_SEO_CTR_NOTES as readonly string[]).includes(code)
    ? `marketing.seo.overview.ctrNotes.${code}`
    : 'marketing.seo.overview.ctrNote';

/**
 * Лучший конкурент запроса — по базе Labs месяца (`bestCompetitorSource =
 * labs`): недельной выдачи нет или она обрезана (органики меньше 10).
 */
export const MARKETING_SEO_FULL_SERP = 10;

export const marketingSeoSerpCut = (
  organic: number | null | undefined,
): boolean =>
  organic !== null &&
  organic !== undefined &&
  organic < MARKETING_SEO_FULL_SERP;

/** Карточек кластеров до «Показать ещё». */
export const MARKETING_SEO_CLUSTERS_PAGE = 12;

/** Тем «Курсов нет, а спрос есть» на графике. */
export const MARKETING_SEO_TOPICS_LIMIT = 12;

/** Строк разрывов за запрос (поповер доказательства, панель). */
export const MARKETING_SEO_GAP_ROWS = 200;

/**
 * Что уходит в «В план» карточки: возможности её запросов, а у кластера
 * без них («курса нет» — возможность одна на тему) — возможность темы с
 * тем же ключом.
 */
export const marketingSeoClusterPlanIds = (
  cluster: { key: string; opportunityIds: ReadonlyArray<string> },
  topicOpportunity: Readonly<Record<string, string | null | undefined>>,
): string[] => {
  if (cluster.opportunityIds.length) return [...cluster.opportunityIds];
  const topic = topicOpportunity[cluster.key];
  return topic ? [topic] : [];
};

/* ── Возможности SEO ──────────────────────────────────────────────────── */

/** Виды возможностей (решения человека «В план» / «Сделано» / «Скрыть»). */
export const MARKETING_SEO_OPPORTUNITY_KINDS = [
  'gap_keyword',
  'missing_family',
  'content_topic',
  'striking_distance',
  'click_decline',
  'ai_absent',
  'aio_not_cited',
  'link_gap',
] as const;

export type MarketingSeoOpportunityKind = typeof MARKETING_SEO_OPPORTUNITY_KINDS[number];

export const isMarketingSeoOpportunityKind = (
  value: string | null | undefined,
): value is MarketingSeoOpportunityKind =>
  Boolean(value) &&
  (MARKETING_SEO_OPPORTUNITY_KINDS as readonly string[]).includes(
    value as string,
  );

export const MARKETING_SEO_OPPORTUNITY_STATUSES = [
  'open',
  'planned',
  'done',
  'dismissed',
] as const;

export type MarketingSeoOpportunityStatus = typeof MARKETING_SEO_OPPORTUNITY_STATUSES[number];

/**
 * Варианты текста возможности: `marketing.seo.opportunities.<вид>.<вариант>`
 * (заголовок, подпись кнопки «Сегодня»). Полноту держит тест локали.
 */
export const MARKETING_SEO_OPPORTUNITY_TEXT_KEYS: Readonly<
  Record<MarketingSeoOpportunityKind, string[]>
> = {
  gap_keyword: ['title', 'titleOwn', 'action'],
  missing_family: ['title', 'action'],
  content_topic: ['title', 'titleCoursePage', 'action'],
  striking_distance: ['title', 'titleGsc', 'action'],
  click_decline: ['title', 'action'],
  ai_absent: ['title', 'action'],
  aio_not_cited: ['title', 'action'],
  link_gap: ['title', 'action'],
};

type RawOpportunity = Omit<IMarketingSeoOpportunity, 'params' | 'evidence'> & {
  params: unknown;
  evidence: unknown;
};

/** Ответ сервера → возможность экрана: JSON разобран, оценка — число. */
export const marketingSeoOpportunityFromRaw = (
  raw: RawOpportunity,
): IMarketingSeoOpportunity => ({
  ...raw,
  params: marketingParseJson(raw.params),
  evidence: marketingParseJson(raw.evidence),
  valuePlnEst: raw.valuePlnEst ?? null,
});

const text = (json: MarketingJson, key: string): string | null => {
  const value = json[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const list = (json: MarketingJson, key: string): string[] => {
  const value = json[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
};

/** Сколько доменов назвать в тексте (`coderslab.pl, kodilla.com +2`). */
const NAMED_DOMAINS = 3;

/** Список доменов одной строкой: первые три и «+N». */
export const marketingSeoDomainsText = (
  domains: ReadonlyArray<string>,
): string => {
  if (!domains.length) return MARKETING_DASH;
  const head = domains.slice(0, NAMED_DOMAINS).join(', ');
  const rest = domains.length - NAMED_DOMAINS;
  return rest > 0 ? `${head} +${rest}` : head;
};

/**
 * Заголовок возможности по виду: подстановки из `params` сервера (числа —
 * в формате языка, строки — как есть, через `{{- …}}`).
 */
export const marketingSeoOpportunityTitle = (
  opportunity: Pick<IMarketingSeoOpportunity, 'kind' | 'params'> &
    Partial<Pick<IMarketingSeoOpportunity, 'keyword' | 'url'>>,
  lang?: string | null,
): IMarketingText => {
  const p = opportunity.params;
  const prefix = `marketing.seo.opportunities.${opportunity.kind}`;
  const keyword = text(p, 'keyword') ?? opportunity.keyword ?? MARKETING_DASH;
  const volume = formatMarketingCount(marketingJsonNumber(p, 'volume'), lang);
  switch (opportunity.kind) {
    case 'gap_keyword': {
      const own = marketingJsonNumber(p, 'own_position');
      const competitors = marketingSeoDomainsText(list(p, 'competitors'));
      return own === null
        ? {
            key: `${prefix}.title`,
            params: { keyword, volume, competitors },
          }
        : {
            key: `${prefix}.titleOwn`,
            params: {
              keyword,
              volume,
              competitors,
              position: formatMarketingCount(own, lang),
            },
          };
    }
    case 'missing_family':
      return {
        key: `${prefix}.title`,
        params: {
          topic: text(p, 'topic') ?? MARKETING_DASH,
          n: formatMarketingCount(marketingJsonNumber(p, 'keywords'), lang),
          volume,
        },
      };
    case 'content_topic':
      // Тема без продаваемого курса, но спрос «узнать» или каталог: статья
      // или страница курса (`suggested_action` сервера).
      return {
        key:
          text(p, 'suggested_action') === 'course_page'
            ? `${prefix}.titleCoursePage`
            : `${prefix}.title`,
        params: {
          topic: text(p, 'topic') ?? MARKETING_DASH,
          n: formatMarketingCount(marketingJsonNumber(p, 'keywords'), lang),
          volume,
        },
      };
    case 'striking_distance': {
      const position = marketingJsonNumber(p, 'position');
      return position === null
        ? {
            key: `${prefix}.titleGsc`,
            params: {
              keyword,
              position: formatMarketingPosition(
                marketingJsonNumber(p, 'gsc_position'),
                lang,
              ),
            },
          }
        : {
            key: `${prefix}.title`,
            params: {
              keyword,
              position: formatMarketingCount(position, lang),
              volume,
            },
          };
    }
    case 'click_decline': {
      const drop = marketingJsonNumber(p, 'drop_pct');
      return {
        key: `${prefix}.title`,
        params: {
          url: text(p, 'url') ?? opportunity.url ?? MARKETING_DASH,
          drop:
            drop === null
              ? MARKETING_DASH
              : formatMarketingPct(drop / 100, lang),
          clicks: formatMarketingCount(marketingJsonNumber(p, 'clicks'), lang),
          prev: formatMarketingCount(
            marketingJsonNumber(p, 'prev_clicks'),
            lang,
          ),
        },
      };
    }
    case 'ai_absent':
      return {
        key: `${prefix}.title`,
        params: {
          question: text(p, 'question') ?? MARKETING_DASH,
          leaders: marketingSeoDomainsText(list(p, 'leaders')),
        },
      };
    case 'aio_not_cited':
      return {
        key: `${prefix}.title`,
        params: {
          keyword,
          cited: marketingSeoDomainsText(list(p, 'cited')),
        },
      };
    case 'link_gap':
      return {
        key: `${prefix}.title`,
        params: {
          domain: text(p, 'domain') ?? MARKETING_DASH,
          targets: marketingSeoDomainsText(list(p, 'links_to')),
        },
      };
    default:
      return {
        key: 'marketing.seo.opportunities.other',
        params: { kind: opportunity.kind },
      };
  }
};

/** Сколько конкурентов назвать в строке доказательства. */
const EVIDENCE_RIVALS = 3;

/**
 * Строка доказательства возможности (карточка «Сегодня»): конкуренты с
 * позициями (разрыв), цитаты AI Overview, показы GSC (почти в топе).
 * Сказать нечего — `null`.
 */
export const marketingSeoOpportunityEvidence = (
  opportunity: Pick<IMarketingSeoOpportunity, 'evidence'>,
  lang?: string | null,
): IMarketingText | null => {
  const e = opportunity.evidence;
  const key = (name: string) => `marketing.seo.opportunities.evidence.${name}`;
  const rivals = Array.isArray(e.competitors)
    ? (e.competitors as unknown[])
        .map((item) => marketingParseJson(item))
        .map((row) => {
          const domain = text(row, 'domain');
          const position = marketingJsonNumber(row, 'position');
          if (!domain) return null;
          return position === null
            ? domain
            : `${domain} (${formatMarketingCount(position, lang)})`;
        })
        .filter((item): item is string => item !== null)
    : [];
  if (rivals.length) {
    return {
      key: key('rivals'),
      params: {
        list: rivals.slice(0, EVIDENCE_RIVALS).join(', '),
      },
    };
  }
  const refs = list(e, 'references');
  if (refs.length) {
    return {
      key: key('aio'),
      params: { list: marketingSeoDomainsText(refs) },
    };
  }
  const impressions = marketingJsonNumber(e, 'gsc_impressions_28d');
  if (impressions !== null) {
    return {
      key: key('gsc'),
      params: {
        impressions: formatMarketingCount(impressions, lang),
        clicks: formatMarketingCount(
          marketingJsonNumber(e, 'gsc_clicks_28d'),
          lang,
        ),
      },
    };
  }
  return null;
};

/** Варианты строки доказательства возможности (тест локали). */
export const MARKETING_SEO_OPPORTUNITY_EVIDENCE_KEYS = ['rivals', 'aio', 'gsc'];

/* ── AI-ответы ────────────────────────────────────────────────────────── */

/**
 * Системы «AI-ответов» (`system` сервера): ChatGPT и Gemini вместе (по
 * умолчанию), каждая отдельно и AI Overview Google (из недельной выдачи).
 */
export const MARKETING_SEO_AI_SYSTEMS = [
  'all',
  'chatgpt',
  'gemini',
  'aio',
] as const;

export type MarketingSeoAiSystem = typeof MARKETING_SEO_AI_SYSTEMS[number];

/** Системы, которые отвечают на вопросы (у AI Overview вопросов нет). */
export const MARKETING_SEO_AI_ANSWER_SYSTEMS = ['chatgpt', 'gemini'] as const;

/** Группы вопросов AI: выбор, условия, сравнение, бренд. */
export const MARKETING_SEO_AI_GROUPS = [
  'choice',
  'constraints',
  'comparison',
  'brand',
] as const;

export type MarketingSeoAiGroup = typeof MARKETING_SEO_AI_GROUPS[number];

/**
 * Срез доли упоминаний (`group` сервера): нейтральные (выбор + условия —
 * по умолчанию), сравнения, бренд. Бренд не смешивается с нейтральными.
 */
export const MARKETING_SEO_AI_SCOPES = [
  'neutral',
  'comparison',
  'brand',
] as const;

export type MarketingSeoAiScope = typeof MARKETING_SEO_AI_SCOPES[number];

/** Аргумент системы для сервера: «все» — пусто (ChatGPT и Gemini). */
export const marketingSeoAiSystemArg = (
  system: MarketingSeoAiSystem,
): string | null => (system === 'all' ? null : system);

/* ── Спрос по месяцам ─────────────────────────────────────────────────── */

export interface IMarketingSeoMonthPoint {
  /** `2026-08` */
  month: string;
  value: number;
}

/**
 * Частотность по месяцам (сервер — от нового к старому, у спроса курса —
 * от старого к новому) → по возрастанию месяца.
 */
export const marketingSeoMonthSeries = (
  points: ReadonlyArray<{ year: number; month: number; searchVolume: number }>,
): IMarketingSeoMonthPoint[] =>
  points
    .map((point) => ({
      month: `${point.year}-${String(point.month).padStart(2, '0')}`,
      value: point.searchVolume,
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

/* ── План статей (.md) ────────────────────────────────────────────────── */

export interface IMarketingSeoPlanSection {
  heading: string;
  lines: string[];
}

/**
 * Markdown плана: заголовок, подзаголовок и разделы со списками. Тексты
 * переведены заранее (модель переводчика не знает).
 */
export const marketingSeoPlanMarkdown = ({
  title,
  subtitle,
  sections,
}: {
  title: string;
  subtitle?: string | null;
  sections: ReadonlyArray<IMarketingSeoPlanSection>;
}): string => {
  const out = [`# ${title}`];
  if (subtitle) out.push('', subtitle);
  sections.forEach((section) => {
    out.push('', `## ${section.heading}`, '');
    section.lines.forEach((line) => out.push(`- ${line}`));
  });
  return `${out.join('\n')}\n`;
};

/** Имя файла плана: `seo-plan-2026-10-09.md`. */
export const marketingSeoPlanFileName = (today: MarketingDate): string =>
  `seo-plan-${today}.md`;

/** Откуда домен в списке SEO: затравка, предложен сервером, вручную. */
export const MARKETING_SEO_DOMAIN_SOURCES = [
  'seed',
  'suggested',
  'manual',
] as const;

/* ── Бюджет ───────────────────────────────────────────────────────────── */

/** Виды прогонов DataForSEO, у которых бывает смета. */
export const MARKETING_SEO_RUN_KINDS = [
  'weekly',
  'monthly',
  'bootstrap',
] as const;

/** Состояния прогона. */
export const MARKETING_SEO_RUN_STATES = [
  'planned',
  'posting',
  'collecting',
  'done',
  'partial',
  'failed',
  'refused_budget',
] as const;

/** Отказы сметы (`refusal`): потолок, лимит прогона, баланс, старые цены. */
export const MARKETING_SEO_REFUSALS = [
  'cap',
  'max_run',
  'balance',
  'price_stale',
  'price_drift',
] as const;

/** Decimal строкой → число; не число — `null`. */
export const marketingSeoUsd = (value: unknown): number | null =>
  typeof value === 'number' || typeof value === 'string'
    ? marketingNumber(value)
    : null;

/** Доля потраченного от потолка, 0..1 (полоса бюджета). */
export const marketingSeoBudgetShare = (
  spent: number | null,
  cap: number | null,
): number | null =>
  spent === null || cap === null || cap <= 0
    ? null
    : Math.min(1, Math.max(0, spent / cap));

/* ── «Что написать»: одна карточка на курс ─────────────────────────────── */

/** Главных запросов в карточке курса. */
export const MARKETING_SEO_FAMILY_TOP_KEYWORDS = 6;

/** Конкурентов в карточке курса. */
export const MARKETING_SEO_FAMILY_RIVALS = 4;

export interface IMarketingSeoFamilyGroup {
  familySlug: string;
  /** Кластеры курса — в порядке сервера (по спросу). */
  clusters: IMarketingSeoCluster[];
  /** Запросов у курса: кластеры не пересекаются по запросам — сумма. */
  keywords: number;
  searchVolume: number;
  /**
   * Оценка денег курса — НАИБОЛЬШАЯ из кластеров, не сумма: близкие запросы
   * («python», «kurs python») ищут одни и те же люди.
   */
  valuePlnEst: number | null;
  /** Главные запросы — по кластерам по очереди, без повторов. */
  topKeywords: string[];
  /** Конкуренты — по домену, число запросов — наибольшее из кластеров. */
  competitors: Array<{ domain: string; keywords: number }>;
  /** Наша страница — первая найденная у кластеров. */
  ownUrl: string | null;
  isSold: boolean;
  planned: number;
}

export interface IMarketingSeoIdeaGroups {
  /** Кластеры с курсом — по курсу, продаваемые первыми. */
  families: IMarketingSeoFamilyGroup[];
  /** Общие темы без курса («gpt», «ai») — отдельно, свёрнуто. */
  general: IMarketingSeoCluster[];
}

/**
 * Кластеры «Что написать» → карточка на курс (главные запросы курса,
 * оценка — наибольшая, а не сумма) и общие темы без курса отдельным
 * списком. Порядок курсов — по первому кластеру; продаваемые — первыми.
 */
export const marketingSeoIdeaGroups = (
  clusters: ReadonlyArray<IMarketingSeoCluster>,
): IMarketingSeoIdeaGroups => {
  const general: IMarketingSeoCluster[] = [];
  const order: string[] = [];
  const bySlug = new Map<string, IMarketingSeoCluster[]>();
  clusters.forEach((cluster) => {
    const slug = cluster.familySlug;
    if (!slug) {
      general.push(cluster);
      return;
    }
    const known = bySlug.get(slug);
    if (known) known.push(cluster);
    else {
      bySlug.set(slug, [cluster]);
      order.push(slug);
    }
  });
  const families = order.map((slug, index) => {
    const members = bySlug.get(slug) as IMarketingSeoCluster[];
    const keywords: string[] = [];
    const depth = Math.max(...members.map((item) => item.topKeywords.length));
    for (let i = 0; i < depth; i += 1) {
      members.forEach((item) => {
        const word = item.topKeywords[i];
        if (word && !keywords.includes(word)) keywords.push(word);
      });
    }
    const rivals = new Map<string, number>();
    members.forEach((item) =>
      item.competitors.forEach((rival) =>
        rivals.set(
          rival.domain,
          Math.max(rivals.get(rival.domain) ?? 0, rival.keywords),
        ),
      ),
    );
    const values = members
      .map((item) => item.valuePlnEst)
      .filter(
        (value): value is number => typeof value === 'number' && value > 0,
      );
    return {
      index,
      group: {
        familySlug: slug,
        clusters: members,
        keywords: members.reduce((sum, item) => sum + item.keywords, 0),
        searchVolume: members.reduce((sum, item) => sum + item.searchVolume, 0),
        valuePlnEst: values.length ? Math.max(...values) : null,
        topKeywords: keywords.slice(0, MARKETING_SEO_FAMILY_TOP_KEYWORDS),
        competitors: Array.from(rivals.entries())
          .map(([domain, n]) => ({ domain, keywords: n }))
          .sort((a, b) => b.keywords - a.keywords)
          .slice(0, MARKETING_SEO_FAMILY_RIVALS),
        ownUrl: members.find((item) => item.ownUrl)?.ownUrl ?? null,
        isSold: members.some((item) => item.isSold),
        planned: members.reduce((sum, item) => sum + item.planned, 0),
      },
    };
  });
  families.sort(
    (a, b) =>
      Number(b.group.isSold) - Number(a.group.isSold) || a.index - b.index,
  );
  return { families: families.map((item) => item.group), general };
};

/* ── Повторное ревью 0103, пакет 2 (P6, P7, C7, C9) ─────────────────────── */

/**
 * Недельных точек, с которых рисуется динамика (видимость, зоны позиций,
 * упоминания AI по неделям). Одна точка — не линия: вместо графика фраза
 * «Динамика появится со второй недели данных».
 */
export const MARKETING_SEO_MIN_DYNAMICS_POINTS = 2;

export const marketingSeoHasDynamics = (points: number): boolean =>
  points >= MARKETING_SEO_MIN_DYNAMICS_POINTS;

/** Зона позиций одной полосой: число запросов и доля от всех в зонах. */
export interface IMarketingSeoZoneShare {
  zone: MarketingSeoZone;
  count: number;
  /** 0..1; запросов нет — 0. */
  share: number;
}

type ZoneCounts = Pick<
  IMarketingSeoZoneWeek,
  'top3' | 'pos4to10' | 'pos11to20' | 'beyond20'
>;

/**
 * «Позиции» с одной неделей данных: вместо стека из одного столбца — одна
 * полоса «Топ-3 / 4–10 / 11–20 / 21+». Числа — те же, что у столбца недели.
 */
export const marketingSeoZoneShares = (
  week: ZoneCounts | null | undefined,
): IMarketingSeoZoneShare[] => {
  if (!week) return [];
  const counts = MARKETING_SEO_ZONES.map((zone) => ({
    zone,
    count: Math.max(0, Number(week[MARKETING_SEO_ZONE_FIELDS[zone]]) || 0),
  }));
  const total = counts.reduce((sum, item) => sum + item.count, 0);
  return counts.map((item) => ({
    ...item,
    share: total > 0 ? item.count / total : 0,
  }));
};

/** Последняя неделя ряда зон (сервер мог отдать в любом порядке). */
export const marketingSeoLatestZoneWeek = <T extends { week: MarketingDate }>(
  weeks: ReadonlyArray<T> | null | undefined,
): T | null =>
  (weeks ?? []).reduce<T | null>(
    (latest, item) => (!latest || item.week > latest.week ? item : latest),
    null,
  );

/**
 * «Конкуренты»: шесть ключевых строк матрицы — видимость, запросов в топ-3
 * и топ-10, средняя позиция, ссылающиеся домены, цитаты AI; остальное —
 * под «Ещё метрики (N)».
 */
export const MARKETING_SEO_COMPETITOR_KEY_METRICS: ReadonlyArray<
  IMarketingSeoCompetitorMetric['id']
> = [
  'sov',
  'top3',
  'top10',
  'avgPosition',
  'referringDomains',
  'aioCitationShare',
];

/**
 * Строки матрицы: свёрнуто — ключевые (в их порядке), развёрнуто — ключевые
 * на прежних местах и остальные ниже (раскрытие не двигает то, что было).
 */
export const marketingSeoCompetitorRows = (
  expanded: boolean,
): IMarketingSeoCompetitorMetric[] => {
  const byId = new Map(
    MARKETING_SEO_COMPETITOR_METRICS.map((metric) => [metric.id, metric]),
  );
  const key = MARKETING_SEO_COMPETITOR_KEY_METRICS.map(
    (id) => byId.get(id) as IMarketingSeoCompetitorMetric,
  );
  if (!expanded) return key;
  return [
    ...key,
    ...MARKETING_SEO_COMPETITOR_METRICS.filter(
      (metric) => !MARKETING_SEO_COMPETITOR_KEY_METRICS.includes(metric.id),
    ),
  ];
};

/** Сколько строк под «Ещё метрики (N)». */
export const MARKETING_SEO_COMPETITOR_MORE =
  MARKETING_SEO_COMPETITOR_METRICS.length -
  MARKETING_SEO_COMPETITOR_KEY_METRICS.length;

/** «Что написать»: карточек тем по умолчанию, дальше — «Показать ещё N». */
export const MARKETING_SEO_IDEAS_TOP = 5;

type IdeaCluster = Pick<IMarketingSeoCluster, 'familySlug' | 'kind'> & {
  kindCounts?: ReadonlyArray<{ value: string; count: number }> | null;
};

/**
 * Карточек тем в сетке «Что написать»: кластеры с курсом собираются в одну
 * карточку на курс; общие темы без курса — не в сетке, а в свёрнутом блоке
 * «Спрос без курса» (их число — на его кнопке).
 */
export const marketingSeoIdeaCardCount = (
  clusters: ReadonlyArray<Pick<IMarketingSeoCluster, 'familySlug'>>,
): number =>
  new Set(
    clusters
      .map((cluster) => cluster.familySlug)
      .filter((slug): slug is string => Boolean(slug)),
  ).size;

/** Есть ли у кластера запросы этого вида (`kindCounts`, иначе — главный вид). */
const clusterHasKind = (cluster: IdeaCluster, kind: string): boolean =>
  cluster.kindCounts && cluster.kindCounts.length
    ? cluster.kindCounts.some((item) => item.value === kind && item.count > 0)
    : cluster.kind === kind;

/**
 * Числа чипов «Что написать» — карточки тем, которые чип покажет в сетке
 * (а не запросы: «Все 413» при восьми видимых карточках вводило в
 * заблуждение). Сервер собирает карточки отбора из строк этого вида, так
 * что карточка курса попадёт в отбор, если у какого-то её кластера есть
 * строка вида (`kindCounts` кластера ответа без вида). Вопросы — сквозной
 * признак строки, его в кластере нет: число — из ответа с `kind=questions`.
 * Виды пересекаются по карточкам (у курса бывают запросы и без страницы, и
 * со страницей ниже 10-й) — экран это подписывает. `null` — ещё не знаем.
 */
export const marketingSeoIdeaChipCounts = (
  base: ReadonlyArray<IdeaCluster> | null | undefined,
  questions: ReadonlyArray<IdeaCluster> | null | undefined,
): Record<'all' | MarketingSeoIdeaKind, number | null> => {
  const of = (kind: Exclude<MarketingSeoIdeaKind, 'questions'>) =>
    base
      ? marketingSeoIdeaCardCount(
          base.filter((cluster) => clusterHasKind(cluster, kind)),
        )
      : null;
  return {
    all: base ? marketingSeoIdeaCardCount(base) : null,
    no_page: of('no_page'),
    weak_page: of('weak_page'),
    no_course: of('no_course'),
    questions: questions ? marketingSeoIdeaCardCount(questions) : null,
  };
};

/** «AI-ответы»: вопросов в таблице до «Показать все». */
export const MARKETING_SEO_AI_QUESTIONS_TOP = 5;

/** Числа строки «От поиска до продажи» — слева направо по пути человека. */
const FUNNEL_FIELDS = [
  'sov',
  'labsEtv',
  'gscClicks',
  'organicEntrances',
  'crmLeads',
  'sales',
  'revenuePln',
] as const;

type FunnelRow = Pick<IMarketingSeoFunnelRow, typeof FUNNEL_FIELDS[number]>;

/**
 * Строка «От поиска до продажи» с данными: хоть одно число больше нуля.
 * Нули и «—» по всей строке («нет курса», ни видимости, ни кликов, ни
 * лидов) — шум: такие строки скрыты, их число — подписью.
 */
export const marketingSeoFunnelRowHasData = (row: FunnelRow): boolean =>
  FUNNEL_FIELDS.some((field) => {
    const value = marketingNumber(row[field] as number | string | null);
    return value !== null && value > 0;
  });

/* ── «Спрос»: сезонность тепловой полосой ─────────────────────────────── */

export interface IMarketingSeoSeasonRow {
  familySlug: string;
  familyName: string;
  isSold: boolean;
  /** Наибольший месяц курса — от него считается цвет ячейки. */
  peak: number;
  /** Месяц `2026-08` → частотность и доля от пика курса (0..1). */
  cells: Readonly<Record<string, { value: number; share: number }>>;
}

export interface IMarketingSeoSeasonHeat {
  /** Месяцы колонок по возрастанию (`2025-10` … `2026-09`). */
  months: string[];
  rows: IMarketingSeoSeasonRow[];
}

/**
 * Сезонность «курс × месяц» (повторное ревью, C9): одиннадцать мини-
 * столбиков на карточку читались плохо — теперь одна строка на курс, цвет
 * ячейки — доля от НАИБОЛЬШЕГО месяца этого курса (у каждого курса свой
 * масштаб: Kubernetes с 366 в пик и Data Science с 41 875 видны одинаково
 * ясно). Месяцы — объединение рядов (Labs мог отдать курсу не все 12);
 * пропуск — «—», а не ноль.
 */
export const marketingSeoSeasonHeat = (
  rows: ReadonlyArray<{
    familySlug: string;
    familyName: string;
    isSold: boolean;
    volumeTrend?: ReadonlyArray<number> | null;
    volumeMonths?: ReadonlyArray<string> | null;
  }>,
): IMarketingSeoSeasonHeat => {
  const months = new Set<string>();
  const out: IMarketingSeoSeasonRow[] = [];
  rows.forEach((row) => {
    const trend = row.volumeTrend ?? [];
    const labels = row.volumeMonths ?? [];
    const points = trend
      .map((value, index) => ({ month: labels[index], value }))
      .filter(
        (point): point is { month: string; value: number } =>
          typeof point.month === 'string' &&
          /^\d{4}-\d{2}$/.test(point.month) &&
          Number.isFinite(point.value),
      );
    if (!points.length) return;
    const peak = Math.max(0, ...points.map((point) => point.value));
    const cells: Record<string, { value: number; share: number }> = {};
    points.forEach((point) => {
      months.add(point.month);
      cells[point.month] = {
        value: point.value,
        share: peak > 0 ? Math.max(0, point.value) / peak : 0,
      };
    });
    out.push({
      familySlug: row.familySlug,
      familyName: row.familyName,
      isSold: row.isSold,
      peak,
      cells,
    });
  });
  return { months: Array.from(months).sort(), rows: out };
};

/** Число коротко для ячейки сезонности: «41,9 тыс.» / «41,9 tys.». */
export const formatMarketingSeoCompact = (
  value: number | null | undefined,
  lang?: string | null,
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return MARKETING_DASH;
  }
  if (Math.abs(value) < 1000) return formatMarketingCount(value, lang);
  return new Intl.NumberFormat(
    lang && lang.toLowerCase().startsWith('pl') ? 'pl-PL' : 'ru-RU',
    { notation: 'compact', maximumFractionDigits: 1 },
  ).format(value);
};

/* ── Вывод «SEO › Обзор» одной фразой ─────────────────────────────────── */

/**
 * «Во сколько раз» для фразы. Округление честное: до 10 — до десятых
 * («в 3,5 раза», не «в 4 раза»), от 10 — целым. Форма слова «раз» выбрана
 * по числу (без плюральных ключей i18next и без `count`): дробь — «раза» /
 * «raza», целое на 2–4 (кроме 12–14) — «раза» / «razy», остальное — «раз» /
 * «razy». Меньше 1,05 — «почти столько же».
 */
export type MarketingSeoTimesForm = 'close' | 'fraction' | 'few' | 'many';

export interface IMarketingSeoTimes {
  text: string;
  form: MarketingSeoTimesForm;
}

export const marketingSeoTimes = (
  ratio: number,
  lang?: string | null,
): IMarketingSeoTimes => {
  if (!Number.isFinite(ratio) || ratio < 1.05) {
    return { text: formatMarketingCount(1, lang), form: 'close' };
  }
  const rounded = ratio < 10 ? Math.round(ratio * 10) / 10 : Math.round(ratio);
  if (!Number.isInteger(rounded)) {
    return { text: formatMarketingSales(rounded, lang), form: 'fraction' };
  }
  const tail10 = rounded % 10;
  const tail100 = rounded % 100;
  const few = tail10 >= 2 && tail10 <= 4 && !(tail100 >= 12 && tail100 <= 14);
  return {
    text: formatMarketingCount(rounded, lang),
    form: few ? 'few' : 'many',
  };
};

/** Ключи сравнения по форме «раз»: отстаём от лидера и опережаем ближайшего. */
const BEHIND_KEYS: Readonly<Record<MarketingSeoTimesForm, string>> = {
  close: 'marketing.insights.seo.overview.behindClose',
  fraction: 'marketing.insights.seo.overview.behindFraction',
  few: 'marketing.insights.seo.overview.behindFew',
  many: 'marketing.insights.seo.overview.behindMany',
};

const AHEAD_KEYS: Readonly<Record<MarketingSeoTimesForm, string>> = {
  close: 'marketing.insights.seo.overview.aheadClose',
  fraction: 'marketing.insights.seo.overview.aheadFraction',
  few: 'marketing.insights.seo.overview.aheadFew',
  many: 'marketing.insights.seo.overview.aheadMany',
};

/** Меньше запросов в наборе — место по видимости ненадёжно («данных мало»). */
export const MARKETING_SEO_INSIGHT_MIN_KEYWORDS = 10;

export interface IMarketingSeoOverviewInsightInput {
  /** Те же строки, что у «Мы и конкуренты» (`overview.domains`). */
  domains: ReadonlyArray<{
    domain: string;
    label: string;
    isOwn: boolean;
    sov?: number | null;
  }>;
  /** Запросов в наборе недели (`overview.keywords`). */
  keywords: number | null | undefined;
  /** Режим обзора: без бренда (по умолчанию) или только брендовые. */
  brand: 'brand' | 'nonbrand';
  lang?: string | null;
}

/**
 * Вывод «SEO › Обзор» одной фразой (повторное ревью, пункт 1): «LearnIT —
 * 5-й из 6 по видимости без бренда: у Kodilla она в 11 раз выше.» Числа —
 * доли видимости «Мы и конкуренты». Место — с учётом ничьих (сколько
 * доменов строго выше). Первые — «у ближайшего, X, она в N раз ниже»;
 * своя видимость 0 — без деления («у нас её нет, у X — 7 %»). Недели нет,
 * своего домена или соперников нет — «данных нет»; запросов в наборе мало
 * — «данных мало».
 */
export const marketingSeoOverviewInsight = ({
  domains,
  keywords,
  brand,
  lang,
}: IMarketingSeoOverviewInsightInput): IMarketingInsight => {
  const ranked = domains
    .filter(
      (row): row is typeof row & { sov: number } =>
        typeof row.sov === 'number' && Number.isFinite(row.sov),
    )
    .sort((a, b) => b.sov - a.sov);
  const own = ranked.find((row) => row.isOwn);
  const rivals = ranked.filter((row) => !row.isOwn);
  if (!keywords || !own || !rivals.length) {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.seo.overview.noData'),
      ],
    };
  }
  if (keywords < MARKETING_SEO_INSIGHT_MIN_KEYWORDS) {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.seo.overview.small', {
          n: formatMarketingCount(keywords, lang),
        }),
      ],
    };
  }
  const scope = marketingInsightPart(
    brand === 'brand'
      ? 'marketing.insights.seo.overview.scopeBrand'
      : 'marketing.insights.seo.overview.scopeNonbrand',
  );
  const n = formatMarketingCount(ranked.length, lang);
  if (ranked[0].sov <= 0) {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.seo.overview.allZero', {
          n,
          scope,
        }),
      ],
    };
  }
  const place = 1 + ranked.filter((row) => row.sov > own.sov).length;
  const rank = marketingInsightPart('marketing.insights.seo.overview.rank', {
    own: own.label,
    place: formatMarketingCount(place, lang),
    n,
    scope,
  });
  let comparison: IMarketingInsightPart;
  if (place === 1) {
    const rival = rivals[0];
    if (rival.sov <= 0) {
      comparison = marketingInsightPart(
        'marketing.insights.seo.overview.aheadRivalZero',
      );
    } else {
      const times = marketingSeoTimes(own.sov / rival.sov, lang);
      comparison = marketingInsightPart(AHEAD_KEYS[times.form], {
        rival: rival.label,
        times: times.text,
      });
    }
  } else {
    const leader = ranked[0];
    if (own.sov <= 0) {
      comparison = marketingInsightPart(
        'marketing.insights.seo.overview.behindOwnZero',
        {
          leader: leader.label,
          value: formatMarketingPct(leader.sov, lang),
        },
      );
    } else {
      const times = marketingSeoTimes(leader.sov / own.sov, lang);
      comparison = marketingInsightPart(BEHIND_KEYS[times.form], {
        leader: leader.label,
        times: times.text,
      });
    }
  }
  let tone: IMarketingInsight['tone'] = 'warn';
  if (place === 1) tone = 'good';
  else if (place > ranked.length / 2) tone = 'bad';
  return { tone, segments: [rank, ': ', comparison, '.'] };
};
